"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const slices = [
  {
    id: 0,
    title: "AI Content",
    fullTitle: "AI Content Generation",
    description: "Create stunning images, videos, and copy powered by leading AI models. Generate content that perfectly matches your brand voice.",
  },
  {
    id: 1,
    title: "Scheduling",
    fullTitle: "Smart Scheduling",
    description: "AI-powered scheduling that posts your content at the perfect time for maximum engagement across all platforms.",
  },
  {
    id: 2,
    title: "Brand Voice",
    fullTitle: "Brand Voice AI",
    description: "Our AI learns your unique brand personality and creates content that sounds authentically like you.",
  },
  {
    id: 3,
    title: "Multi-Platform",
    fullTitle: "Multi-Platform",
    description: "Publish to Instagram, TikTok, Facebook, LinkedIn, Pinterest, YouTube, and X with a single click.",
  },
  {
    id: 4,
    title: "Analytics",
    fullTitle: "Analytics & Insights",
    description: "Track performance across all platforms with detailed analytics. Understand what works and optimize your strategy.",
  },
];

export function InclinedSlices() {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      setIsLargeScreen(window.innerWidth >= 1280);
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  const handleToggle = (index: number) => {
    setExpandedIndex(index);
  };

  return (
    <section
      className="flex flex-col justify-center relative overflow-hidden"
      style={{
        paddingTop: 'clamp(5rem, 12vh, 8rem)',
        paddingBottom: 'clamp(5rem, 12vh, 8rem)'
      }}
    >
      <div className="max-w-6xl mx-auto px-6 w-full">
        {/* Header - left aligned with proportional spacing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left"
          style={{ marginBottom: 'clamp(2rem, 6vh, 4rem)' }}
        >
          <span className="text-accent text-sm font-medium uppercase tracking-wider mb-4 block">
            Why Choose Lateral
          </span>
          <h2
            className="font-bold text-foreground mb-4"
            style={{ fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)' }}
          >
            Why teams <span className="text-gradient">choose us</span>
          </h2>
          <p
            className="text-foreground-secondary"
            style={{
              fontSize: 'clamp(0.875rem, 1vw + 0.25rem, 1rem)',
              maxWidth: 'clamp(180px, 20vw, 280px)'
            }}
          >
            Explore the features built for modern marketers.
          </p>
        </motion.div>

        {/* Mobile: Vertical Accordion - shown on small screens */}
        <div className="md:hidden flex flex-col gap-0">
          {slices.map((slice, index) => {
            const isExpanded = expandedIndex === index;
            const isFirst = index === 0;

            return (
              <motion.div
                key={slice.id}
                onClick={() => handleToggle(index)}
                className="relative cursor-pointer"
              >
                {/* Skewed container */}
                <div
                  className="relative slice-glass"
                  style={{
                    transform: "skewY(-3deg)",
                    transformOrigin: "left center",
                  }}
                >
                  {/* Background gradient - visible when expanded */}
                  <motion.div
                    className="absolute inset-0 slice-expanded-gradient"
                    animate={{ opacity: isExpanded ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      background: "linear-gradient(90deg, rgba(16, 185, 129, 0.15) 0%, rgba(163, 230, 53, 0.05) 100%)",
                    }}
                  />

                  {/* Top border line */}
                  {isFirst && (
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-foreground/10" />
                  )}

                  {/* Bottom border line */}
                  <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-foreground/10" />

                  {/* Content - significantly more padding when expanded */}
                  <motion.div
                    className="relative px-3"
                    animate={{
                      paddingTop: isExpanded ? "2.5rem" : "1.25rem",
                      paddingBottom: isExpanded ? "2.5rem" : "1.25rem",
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      transform: "skewY(3deg)",
                    }}
                  >
                    {/* Header row */}
                    <motion.div
                      className="flex items-center justify-between ml-2"
                      animate={{ rotate: isExpanded ? 0 : -3 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-foreground/20 text-xs font-medium slice-number">
                          0{slice.id + 1}
                        </span>
                        <h3
                          className="font-bold text-accent"
                          style={{ fontSize: 'clamp(1rem, 2vw + 0.25rem, 1.25rem)' }}
                        >
                          {isExpanded ? slice.fullTitle : slice.title}
                        </h3>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 45 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-7 h-7 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/40 shrink-0"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </motion.div>
                    </motion.div>

                    {/* Description - animated expand with more padding */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <p
                            className="text-foreground-secondary leading-relaxed pt-4 pr-8 ml-2"
                            style={{ fontSize: 'clamp(0.875rem, 1.5vw + 0.125rem, 1rem)' }}
                          >
                            {slice.description}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Tablet: 768-1280px - Horizontal slices with click */}
        <div
          className="hidden md:flex xl:hidden relative overflow-visible mx-4 lg:mx-8"
          style={{ height: 'clamp(320px, 40vh, 450px)' }}
        >
          {slices.map((slice, index) => {
            const isExpanded = expandedIndex === index;
            const isLast = index === slices.length - 1;

            return (
              <motion.div
                key={slice.id}
                onClick={() => handleToggle(index)}
                animate={{
                  flex: isExpanded ? 4.5 : 1,
                }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className="relative cursor-pointer h-full"
              >
                {/* Full skewed slice container */}
                <div
                  className="absolute inset-0 slice-glass"
                  style={{
                    transform: "skewX(-6deg)",
                    transformOrigin: "center center",
                  }}
                >
                  {/* Background gradient */}
                  <motion.div
                    className="absolute inset-0 slice-expanded-gradient"
                    animate={{
                      opacity: isExpanded ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      background: "linear-gradient(180deg, rgba(16, 185, 129, 0.15) 0%, rgba(163, 230, 53, 0.05) 100%)",
                    }}
                  />

                  {/* Left border line */}
                  <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-foreground/10" />

                  {/* Right border line (only for last) */}
                  {isLast && (
                    <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-foreground/10" />
                  )}
                </div>

                {/* Content container */}
                <div className="relative h-full flex flex-col justify-between py-4 lg:py-5 z-10">
                  {/* Top section */}
                  <div className="px-3 lg:px-4">
                    {isExpanded ? (
                      /* Expanded: Title horizontal at top with X icon */
                      <div className="flex items-start justify-between">
                        <motion.h3
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.25 }}
                          className="font-bold text-accent whitespace-nowrap pl-3 lg:pl-4"
                          style={{
                            transform: "skewX(-6deg)",
                            fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)'
                          }}
                        >
                          {slice.fullTitle}
                        </motion.h3>
                        <motion.div
                          initial={{ rotate: 0 }}
                          animate={{ rotate: 45 }}
                          transition={{ duration: 0.3 }}
                          className="w-6 h-6 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/40 shrink-0"
                        >
                          <Plus className="w-3 h-3" />
                        </motion.div>
                      </div>
                    ) : (
                      /* Collapsed: Title vertical on left + icon on right */
                      <>
                        {/* Plus icon at top right */}
                        <motion.div
                          initial={{ rotate: 45 }}
                          animate={{ rotate: 0 }}
                          transition={{ duration: 0.3 }}
                          className="absolute top-4 right-[-6px] w-6 h-6 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/40"
                        >
                          <Plus className="w-3 h-3" />
                        </motion.div>
                        {/* Vertical title on LEFT side */}
                        <motion.h3
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.25 }}
                          className="absolute font-bold text-accent whitespace-nowrap"
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg) skewX(-6deg)",
                            top: "12px",
                            left: "12px",
                            fontSize: 'clamp(0.75rem, 1vw + 0.125rem, 0.875rem)'
                          }}
                        >
                          {slice.title}
                        </motion.h3>
                      </>
                    )}
                  </div>

                  {/* Middle: Description - only visible when expanded */}
                  <div className="flex-1 flex items-center pl-4 lg:pl-5 pr-3">
                    {isExpanded && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="text-foreground-secondary leading-relaxed"
                        style={{
                          transform: "skewX(-6deg)",
                          fontSize: 'clamp(0.75rem, 1vw + 0.125rem, 0.875rem)',
                          maxWidth: 'clamp(150px, 15vw, 200px)'
                        }}
                      >
                        {slice.description}
                      </motion.p>
                    )}
                  </div>

                  {/* Bottom: Number */}
                  <div className="px-4">
                    <span className="text-foreground/20 text-xs font-medium slice-number">
                      0{slice.id + 1}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Desktop: 1280px+ - Full size with hover */}
        <div
          className="hidden xl:flex relative overflow-visible"
          style={{ height: 'clamp(400px, 45vh, 550px)' }}
        >
          {slices.map((slice, index) => {
            const isExpanded = expandedIndex === index;
            const isLast = index === slices.length - 1;

            return (
              <motion.div
                key={slice.id}
                onMouseEnter={() => handleToggle(index)}
                animate={{
                  flex: isExpanded ? 5 : 1,
                }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className="relative cursor-pointer h-full"
              >
                {/* Full skewed slice container */}
                <div
                  className="absolute inset-0 slice-glass"
                  style={{
                    transform: "skewX(-6deg)",
                    transformOrigin: "center center",
                  }}
                >
                  {/* Background gradient */}
                  <motion.div
                    className="absolute inset-0 slice-expanded-gradient"
                    animate={{
                      opacity: isExpanded ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      background: "linear-gradient(180deg, rgba(16, 185, 129, 0.15) 0%, rgba(163, 230, 53, 0.05) 100%)",
                    }}
                  />

                  {/* Left border line */}
                  <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-foreground/10" />

                  {/* Right border line (only for last) */}
                  {isLast && (
                    <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-foreground/10" />
                  )}
                </div>

                {/* Content container */}
                <div className="relative h-full flex flex-col justify-between py-6 z-10">
                  {/* Top section */}
                  <div className="px-5">
                    {isExpanded ? (
                      /* Expanded: Title horizontal at top with X icon */
                      <div className="flex items-start justify-between">
                        <motion.h3
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.25 }}
                          className="font-bold text-accent whitespace-nowrap pl-8"
                          style={{
                            transform: "skewX(-6deg)",
                            fontSize: 'clamp(1.125rem, 1.5vw, 1.375rem)'
                          }}
                        >
                          {slice.fullTitle}
                        </motion.h3>
                        <motion.div
                          initial={{ rotate: 0 }}
                          animate={{ rotate: 45 }}
                          transition={{ duration: 0.3 }}
                          className="w-8 h-8 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/40 shrink-0"
                        >
                          <Plus className="w-4 h-4" />
                        </motion.div>
                      </div>
                    ) : (
                      /* Collapsed: Title vertical on left + icon on right */
                      <>
                        {/* Plus icon at top right */}
                        <motion.div
                          initial={{ rotate: 45 }}
                          animate={{ rotate: 0 }}
                          transition={{ duration: 0.3 }}
                          className="absolute top-6 right-[-8px] w-8 h-8 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/40"
                        >
                          <Plus className="w-4 h-4" />
                        </motion.div>
                        {/* Vertical title on LEFT side */}
                        <motion.h3
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.25 }}
                          className="absolute font-bold text-accent whitespace-nowrap"
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg) skewX(-6deg)",
                            top: "24px",
                            left: "24px",
                            fontSize: 'clamp(1rem, 1.25vw, 1.25rem)'
                          }}
                        >
                          {slice.title}
                        </motion.h3>
                      </>
                    )}
                  </div>

                  {/* Middle: Description - only visible when expanded */}
                  <div className="flex-1 flex items-center pl-10 pr-5">
                    {isExpanded && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="text-foreground-secondary leading-relaxed"
                        style={{
                          transform: "skewX(-6deg)",
                          fontSize: 'clamp(0.875rem, 1vw + 0.125rem, 1.0625rem)',
                          maxWidth: 'clamp(280px, 20vw, 350px)'
                        }}
                      >
                        {slice.description}
                      </motion.p>
                    )}
                  </div>

                  {/* Bottom: Number */}
                  <div className="px-6">
                    <span
                      className="text-foreground/20 font-medium slice-number"
                      style={{ fontSize: 'clamp(0.75rem, 1vw, 0.875rem)' }}
                    >
                      0{slice.id + 1}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
